import {
  FetchCompactedSitesParams,
  queryCompactedSites,
  mapCompactedSites,
  CompactedSite,
  SiteSummary,
  FetchSiteSummaryParams,
  querySiteSummary,
  mapSiteSummary,
  mapQuerySites,
  querySites
} from './queries'
import { Drive } from '../../services/api/apiTypes'
import { SortParams } from '../../utils/sortUtil'
import graphqlService from '../../services/graphqlService'
import apiService from '../../services/api/apiService'
import { FilterParams } from '../../interfaces'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type SiteItem = {
  id: string
  siteId: string
  name: string
  size: string
  owners: string[]
  lastModified: string
  onLegalHold: boolean
  retentionLabel: string
  status: string
}

export type SitesListSettings = {
  list?: Drive[]
  total?: number
  sort: SortParams
}

type SitesSliceState = {
  compactedSites?: CompactedSite[]
  compactedSitesTotal?: number
  siteSummary?: SiteSummary

  list: SiteItem[]
  total: number
  owners: string[]
  totalOwners: number
}

export const initialState: SitesSliceState = {
  list: [],
  total: 0,
  owners: [],
  totalOwners: 0
}

export const ACTION_COMPACTED_SITES_FETCH = 'sites/fetchCompactedSites'
export const fetchCompactedSites = createAsyncThunk(
  ACTION_COMPACTED_SITES_FETCH,
  async (params: FetchCompactedSitesParams) => {
    const resultRaw = await graphqlService.execute(queryCompactedSites(params))
    return mapCompactedSites(resultRaw)
  }
)

export const ACTION_SITE_SUMMARY_FETCH = 'sites/fetchSiteSummary'
export const fetchSiteSummary = createAsyncThunk(
  ACTION_SITE_SUMMARY_FETCH,
  async (params: FetchSiteSummaryParams) => {
    const resultRaw = await graphqlService.execute(querySiteSummary(params))
    return mapSiteSummary(resultRaw)
  }
)

export type FetchSitesParams = {
  datasourceId: string
  page: number
  pageSize: number
  filters?: FilterParams
  searchQuery?: string
}

export const ACTION_FETCH_SITES = 'sites/list'
export const fetchSites = createAsyncThunk(ACTION_FETCH_SITES, async (params: FetchSitesParams) => {
  const resultRaw = await graphqlService.execute(querySites(params))
  return mapQuerySites(resultRaw)
})

export type FetchSiteOwnerParams = {
  datasourceId?: string
  page?: number
  pageSize?: number
  searchFilter?: any
}

export const ACTION_FETCH_SITE_OWNERS = 'site/owners'
export const fetchSiteOwners = createAsyncThunk(
  ACTION_FETCH_SITE_OWNERS,
  async (params: FetchSiteOwnerParams) => {
    return await apiService.getSiteOwners(params)
  }
)

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    clearCompactedSites: (state) => {
      state.compactedSites = undefined
      state.compactedSitesTotal = undefined
    },
    resetSites: (state) => {
      state.list = []
      state.total = 0
    },
    resetSiteOwners: (state) => {
      state.owners = initialState.owners
      state.totalOwners = initialState.totalOwners
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCompactedSites.fulfilled, (state, action) => {
      state.compactedSites = action.payload.list
      state.compactedSitesTotal = action.payload.total
    })
    builder.addCase(fetchSiteSummary.fulfilled, (state, action) => {
      state.siteSummary = action.payload
    })
    builder.addCase(fetchSites.fulfilled, (state, { payload }) => {
      state.list = payload.list
      state.total = payload.total
    })
    builder.addCase(fetchSiteOwners.fulfilled, (state, { payload, meta }) => {
      const resetOnFirstSearch =
        meta.arg.page === 1 && meta.arg.searchFilter && meta.arg.searchFilter.length > 0
      state.owners = resetOnFirstSearch ? payload.list : [...(state.owners ?? []), ...payload.list]
      state.totalOwners = payload.total
    })
  }
})

export const { clearCompactedSites, resetSites, resetSiteOwners } = sitesSlice.actions

export default sitesSlice.reducer
